import * as React from "react";

import { AddressInput } from "./AddressInput";
import { Block } from "../Block";
import { Button } from "../Button";
import { Checkbox } from "baseui/checkbox";
import { FormControl } from "../Input";
import { HorizontalSpacer } from "kanopi-components/SpaceWrapper/HorizontalSpacer";
import { LabelMedium } from "kanopi-components/typography";
import { ManualAddressForm } from "./ManualAddressForm";
import { Spinner } from "baseui/spinner";
import { VerticalSpacer } from "kanopi-components/SpaceWrapper/VerticalSpacer";
import { identity } from "../utils/functional";
import { isObject } from "../utils/primitives";
import { useToggle } from "../hooks/toggle";

// Define the types for the value prop
type AddressValue = {
  isManual?: boolean;
  streetLine?: string;
  suburb?: string;
  state?: string;
  postcode?: string;
};

// Define the props for your component
type YourComponentProps = {
  value?: AddressValue;
  onChange?: (value: AddressValue) => void;
  onValidating?: (value: unknown) => void;
  [restProps: string]: unknown; // This captures the rest of the props
  climateDataSource?: "finaddress" | "google" | "NONE";
};

const AddressForm = ({
  value,
  onChange = identity,
  onValidating = identity,
  // climateDataSource = "finaddress", // TODO ?reimplement?
  ...restProps
}: YourComponentProps) => {
  const [isManual, toggleManual] = useToggle(value?.isManual);
  const [climateDataLoading, setClimateDataLoading] = React.useState(null);
  const [climateData, setClimateData] = React.useState(null);

  // const formattedAddress = `${value?.streetLine} ${value?.suburb} ${value?.state} ${value?.postcode}`;

  const handleToggle = () => {
    if (isManual) {
      // Reset the value and the validating flag. This ensures no
      // bleed between a potentially modified manual address and an
      // already validated map address.
      onChange(undefined);
      onValidating(false);
    }
    toggleManual();
  };

  const getClimateData = async (formattedAddress) => {
    const apiToken = globalThis.settings.FIN_ADDRESS_API_KEY;
    const apiUrl = `https://www.finaddress.com.au/api/AddressSearch/${encodeURI(
      formattedAddress
    )}`;

    try {
      setClimateDataLoading(true);
      const response = await fetch(apiUrl, {
        method: "GET",
        headers: {
          Token: `${apiToken}`,
          "Content-Type": "application/json",
        },
      });
      if (response.ok) {
        const data = await response.json();
        const pid = data[0]["GNAF_PID"];
        const climateUrl = `https://www.finaddress.com.au/api/AddressFinderController/${pid}`;
        const climateResponse = await fetch(climateUrl, {
          method: "GET",
          headers: {
            Token: `${apiToken}`,
            "Content-Type": "application/json",
            GNAF_Version: "202308",
          },
        });
        const climateData = await climateResponse.json();
        setClimateDataLoading(false);
        const filteredData = filterClimateData(climateData);
        setClimateData(filteredData);
        setClimateDataLoading(false);
        return climateData;
      } else {
        console.error("Error fetching climate data:", response.statusText);
      }
    } catch (error) {
      console.error("Error:", error.message);
    }
  };

  const showWithClimateData = restProps?.displayFinity;

  return (
    <>
      {!isManual && (
        <VerticalSpacer>
          {showWithClimateData ? (
            <InlineAddressForm
              value={value}
              onChange={onChange}
              onValidating={onValidating}
              getClimateData={getClimateData}
              integrations={restProps?.integrations ?? []}
              {...restProps}
            />
          ) : (
            <InlineAddressForm
              value={value}
              onChange={onChange}
              onValidating={onValidating}
              {...restProps}
            />
          )}

          <Block display="flex" justifyContent="center" marginTop="scale300">
            <Button
              onClick={handleToggle}
              kind={Button.KIND.tertiary}
              size={Button.SIZE.mini}
            >
              Enter address manually
            </Button>
          </Block>
          {showWithClimateData && climateData?.length > 0 && (
            <ClimateDataDisplay
              climateData={climateData}
              climateDataLoading={climateDataLoading}
            />
          )}
        </VerticalSpacer>
      )}
      {!!isManual && (
        <>
          <ManualAddressForm value={value} onChange={onChange} {...restProps} />
          <Block display="flex" justifyContent="center" marginTop="scale300">
            <Button
              onClick={handleToggle}
              kind={Button.KIND.tertiary}
              size={Button.SIZE.mini}
            >
              Find your address on a map
            </Button>
          </Block>
        </>
      )}
    </>
  );
};

function InlineAddressForm(props: unknown) {
  const { label, help, error, getClimateData, ...restProps } = props;
  // TODO: Need to reset the error to something other than an object
  // from the manual address input.
  const _error = isObject(error) ? null : error;
  return (
    <FormControl label={label} help={help} error={_error}>
      <AddressInput {...restProps} getClimateData={getClimateData} />
    </FormControl>
  );
}

const filterClimateData = (climateData = []) => {
  const keys = [
    // Bushfire
    "ICA_Zone",
    "cyclone_non_surge_risk_score",
    "bushfire_risk_score",
    "bushfire_veg_risk_avg_100m",
    "Storm_rate_SME_Bld",
    "storm_rain0",
    "storm_wind0t10",
    "storm_hail_days_0_2",
    "storm_risk_score_SME_Bld",
    "Flood_FL_rate_SME_Bld",
    "flood_FL_ARI_GL",
    "coastal_distance_to_coast",
  ];
  const filteredData = climateData?.Elements?.filter((item) =>
    keys.includes(item.Field)
  );

  return filteredData;
};

// TODO types
const ClimateDataDisplay = ({ climateData = [], climateDataLoading }: unknown) => {
  const humanize = (str) => {
    const frags = str?.split("_");
    for (let i = 0; i < frags.length; i++) {
      frags[i] = frags[i].charAt(0).toUpperCase() + frags[i].slice(1);
    }
    return frags.join(" ");
  };
  const chunkArray = (arr = [], chunkSize) => {
    const chunks = [];
    const _chunkSize = Math.ceil(arr.length / chunkSize);
    for (let i = 0; i < arr.length; i += _chunkSize) {
      const chunk = arr.slice(i, i + _chunkSize);
      chunks.push(chunk);
    }
    return chunks;
  };
  // const splitData = chunkArray(climateData, climateData?.length > 5 ? 2 : 1);
  const dataMap = [
    {
      title: "Bushfire",
      key: "bushfire",
    },
    {
      title: "Cyclone",
      key: "cyclone",
    },
    {
      title: "Flood",
      key: "flood",
    },
    {
      title: "Storm",
      key: "storm",
    },
    {
      title: "Coastal",
      key: "coastal",
    },
  ];
  return (
    <VerticalSpacer>
      {climateDataLoading && (
        <VerticalSpacer>
          <Spinner />
          <p>Fetching this properties risk data</p>
        </VerticalSpacer>
      )}
      {!climateDataLoading &&
        dataMap?.map((item, index) => {
          const _climateData = climateData?.filter((data) => {
            return data?.Product?.includes(item?.key);
          });
          const uniqueData = [
            ...new Map(_climateData.map((item) => [item["Field"], item])).values(),
          ];
          const splitData = chunkArray(uniqueData, uniqueData?.length > 5 ? 2 : 1);
          return (
            <VerticalSpacer key={index}>
              <LabelMedium checked={true} disabled style={{ paddingTop: "20px" }}>
                {item?.title}
              </LabelMedium>
              <Block display="flex">
                {splitData?.map((chunk, index) => {
                  return (
                    <HorizontalSpacer key={index} justifyContent="left">
                      {chunk?.map((item, index) => {
                        return (
                          <Checkbox key={index} checked={true} disabled>
                            {humanize(item?.Field)}
                          </Checkbox>
                        );
                      })}
                    </HorizontalSpacer>
                  );
                })}
              </Block>
            </VerticalSpacer>
          );
        })}
    </VerticalSpacer>
  );
};

export { AddressForm };
